<template>
  <PageNotFound v-if="showNotFound" />
  <div v-else id="rev_view" class="px-10">
    <div
      v-if="isLoading"
      style="height: Calc(100vh - 64px)"
      class="d-flex align-center justify-center"
    >
      <LoaderSpinner />
    </div>
    <v-container v-else class="ma-0 pa-0">
      <Rocket ref="rocket" />

      <RevViewBackButton class="my-2" style="margin-left: 1px" />

      <SummaryActionable
        v-if="isAiSummarizeEnabled && !isEditMode"
        isButton
        :text="
          $t(
            isAiTasksSuggestionEnabled
              ? 'ai_features.ai_assistant'
              : 'common.summarize_this_rev'
          )
        "
        @click="handleSummaryActionable"
      />

      <div
        v-if="showCompletedFromPlannerAlert && isPlannerBadgesEnabled"
        class="planner-alert mb-2 px-4 d-flex justify-space-between align-center"
      >
        <div class="d-flex align-center">
          <img class="planner-icon" alt="planner-icon" :src="PlannerIcon" />
          <p class="mb-0 ml-4 label white--text">
            {{ $t('common.completed_from_planner_message') }}
          </p>
        </div>
        <a
          class="mb-0 cursor-pointer font-weight-500 font-size-12 white--text link-to-planner"
          target="_blank"
          rel="noopener noreferrer"
          :href="linkToPlanner"
        >
          {{ $t('common.open_in_planner') }}
        </a>
      </div>
      <Alert
        v-if="isTranslationEnabled && !isEditMode"
        :vuetifyProps="{
          color: 'blue',
          dismissible: !isTranslating,
          icon: aiFeaturesAreEnabled ? null : 'mdi-translate',
        }"
        :disableButton="isTranslating"
        :buttonText="translateButtonText"
        :text="translateText"
        @buttonClick="toggleTranslations"
      >
        <template v-slot:customIcon>
          <AIIcon v-if="aiFeaturesAreEnabled" iconSize="15" />
        </template>
      </Alert>
      <slot
        :isTranslating="isTranslating"
        :isTranslated="Boolean($route.query.isTranslated)"
        :isEditMode="isEditMode"
        :organizationId="organizationId"
        :accountSiteId="accountSiteId"
        :accountId="accountId"
        :edit="permissions.edit"
        :canComplete="permissions.roleCanComplete"
        :delete="permissions.delete"
        :isApproved="isApproved"
        :isTranslationEnabled="isTranslationEnabled"
        :validate="permissions.validate"
        :menuOptions="menuOptions"
        :revChanged="revChanged"
        :revId="revId"
        :errors="errors"
        :revTags="rev.location.tags"
        :saveRev="saveRev"
        :completeRev="handleCompleteRev"
        :likeRev="likeRev"
        :unlikeRev="unlikeRev"
        :approveRev="approveRev"
        :disapproveRev="disapproveRev"
        :implementRev="handleImplementRev"
        :highlightRev="highlightRev"
        :setValidationRequest="setValidationRequest"
        :setCancelRequest="setCancelRequest"
        :handleCancelClick="handleCancelClick"
        :setEditMode="setEditMode"
        :translateRev="toggleTranslations"
        :setViewMode="setViewMode"
        :setRevChanged="setRevChanged"
        :finishRev="finishRev"
        :isSaving="isSaving"
        :hasCompleteError="hasCompleteError"
        :removeApproval="permissions.removeApproval"
        :approve="permissions.approve"
        :revApprovals="rev.approvals.length || 0"
        :revType="revType"
        :isImplementedValidated="isImplementedValidated"
        :hasRequestValidation="hasRequestValidation"
        :canHighlight="permissions.canHighlight"
        :canDoFinancialValidation="canDoFinancialValidation"
        :isLiked="isLiked"
        :implement="permissions.implement"
        :showImplementError="showImplementError"
        :plannerIntegration="plannerIntegration"
        :replicateRev="replicateRev"
        :downloadPowerpoint="handleDownloadPowerpoint"
      />
    </v-container>

    <RevCompletedModal
      :show="showNewCompletedModal"
      :revTeam="revTeam"
      :hasImplementPermission="permissions.implement"
      :organizationId="organizationId"
      :isImplemented="isImplementedValidated"
      @implementRev="handleImplementRev"
      @setCoach="handleSetCoach"
      @goToReview="goToReview(true, 'revCompletedModal')"
      @close="handleCloseCompleteModal"
    />

    <MissingCoachDialog
      :show="showMissingCoachDialog"
      :revTeam="revTeam"
      :organizationId="organizationId"
      @setCoach="handleSetCoach"
      @complete="completeRev"
      @close="showMissingCoachDialog = false"
    />

    <ChangedModalStatus
      :onlyShowMessage="showOnlyMessage"
      :placeholder="$t('common.cancel_rev')"
      :show="showCancelModal"
      :status="$constants.revStatus.canceled"
      :subtitle="changedModalStatustext.subtitle"
      :title="changedModalStatustext.title"
      @updateModalStatus="handleModalStatusChanged($event)"
      @hideModal="handleHideChangeStatusModal"
    />
    <DeleteConfirmationModal
      :show="showConfirmRemoveImplementationDialog"
      :isLoading="isSaving"
      :title="$t('rev.remove_implementation')"
      :text="$t('rev.remove_implementation_text')"
      :confirmText="$t('rev.remove_implementation_confirm')"
      @delete="implementRev($event, 'removeImplementationModal')"
      @cancel="handleCancelRemoveImplementation"
    />

    <DownloadPowerpointModal
      v-if="showPowerpointModal"
      @hide="showPowerpointModal = false"
      @confirm="confirmPowerpointDownload"
    />
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import _ from 'lodash'
import confetti from 'canvas-confetti'

import mixin from '@/mixins/mixin.vue'
import trackerMixin from '@/mixins/tracking/trackerMixin.vue'
import revPermissionsMixin from '@/mixins/revPermissionsMixin'
import access from '@/lib/access'
import eventHub, { HUB_EVENTS } from '@/lib/event-hub'
import LANGUAGES_DICTIONARY from '@/locales/dictionary.json'
import api from '@/api'

import LoaderSpinner from '@/components/utils/LoaderSpinner.vue'
import PageNotFound from '@/components/utils/PageNotFound.vue'
import Alert from '@/components/Alert.vue'

import RevCompletedModal from '@/components/Rev/RevCompletedModal.vue'
import MissingCoachDialog from '@/components/Rev/MissingCoachDialog.vue'
import ChangedModalStatus from '@/containers/Modals/ChangedModalStatus.vue'
import RevViewBackButton from '@/components/RevViewBackButton.vue'
import DeleteConfirmationModal from '@/components/Modals/DeleteConfirmationModal.vue'
import Rocket from '@/components/Rev/Rocket.vue'
import PlannerIcon from '@/assets/images/planner-icon-white.png'
import SummaryActionable from '@/components/AiAssistant/SummaryActionable.vue'
import AIIcon from '@/components/AiAssistant/AIIcon.vue'
import DownloadPowerpointModal from '@/components/Rev/DownloadPowerpointModal.vue'

export default {
  name: 'RevViewWrapper',

  mixins: [mixin, trackerMixin, revPermissionsMixin],

  components: {
    LoaderSpinner,
    PageNotFound,
    Alert,
    ChangedModalStatus,
    RevViewBackButton,
    RevCompletedModal,
    MissingCoachDialog,
    DeleteConfirmationModal,
    Rocket,
    SummaryActionable,
    AIIcon,
    DownloadPowerpointModal,
  },

  data() {
    return {
      showImplementError: false,
      initialRev: _.cloneDeep(this.rev),
      isEditMode: this.mode === 'edit',
      showNotFound: false,
      isLoading: false,
      isSaving: false,
      activeTab: Number(this.$route.query?.tabIndex ?? 0),
      revChanged: false,
      errors: {
        site: false,
      },
      isTranslating: false,
      hasCompleteError: false,
      showNewCompletedModal: false,
      showMissingCoachDialog: false,
      showCancelModal: false,
      revToUpdate: {},
      showConfirmRemoveImplementationDialog: false,
      showOnlyMessage: false,
      showPowerpointModal: false,
      plannerIntegration: null,
      PlannerIcon,
      showSummary: false,
    }
  },

  props: {
    revType: {
      type: String,
      required: true,
    },
    organizationId: {
      type: String,
      required: true,
    },
    revId: {
      type: String,
      required: true,
    },
    mode: {
      type: String,
      required: false,
    },
    rev: {
      type: Object,
      required: true,
    },
    resetRev: {
      type: Function,
      required: true,
    },
    get: {
      type: Function,
      required: true,
    },
    update: {
      type: Function,
      required: true,
    },
    like: {
      type: Function,
      required: true,
    },
    highlight: {
      type: Function,
      required: false,
    },
    approve: {
      type: Function,
      required: false,
    },
    disapprove: {
      type: Function,
      required: false,
    },
    createValidationRequest: {
      type: Function,
      required: false,
    },
    cancelValidationRequest: {
      type: Function,
      required: false,
    },
    complete: {
      type: Function,
      required: false,
    },
    unlike: {
      type: Function,
      required: true,
    },
    goToTab: {
      type: Function,
      required: true,
    },
    translate: {
      type: Function,
      required: false,
    },
    setCoach: {
      type: Function,
      required: false,
    },
    canCompleteRev: Boolean,
  },

  watch: {
    async $route(to, from) {
      if (from.params.revId !== to.params.revId) {
        await this.initRevView()
        this.goToStep(0, 'initial')
      }
    },
  },

  async created() {
    await this.initRevView()
    if (this.isPlannerBadgesEnabled) {
      this.getRevIntegrations()
    }

    eventHub.$on(
      HUB_EVENTS.REV_VIEW.BEFORE_ROUTE_LEAVE,
      this.checkChangesBeforeLeaving
    )
  },

  computed: {
    ...mapGetters([
      'account',
      'organization',
      'language',
      'availableFeatures',
      'organizationSiteGroups',
    ]),

    permissions() {
      return this.getPermissions({
        rev: this.rev,
        account: this.account,
        isTranslated: this.isTranslated,
        organizationSiteGroups: this.organizationSiteGroups,
      })
    },

    isTranslated() {
      return Boolean(this.$route?.query?.isTranslated)
    },

    aiFeaturesAreEnabled() {
      return access.aiFeaturesAreEnabled(this.account, this.organization)
    },

    isAiSummarizeEnabled() {
      return (
        this.aiFeaturesAreEnabled &&
        this.$isFeatureEnabled(this.$featuresKeys.aiSummarize)
      )
    },

    isAiTasksSuggestionEnabled() {
      return (
        this.aiFeaturesAreEnabled &&
        this.$isFeatureEnabled(this.$featuresKeys.aiTasksSuggestion)
      )
    },

    coachIsRequired() {
      return Boolean(this.getCoachRequirement({ rev: this.rev }).isRequired)
    },

    revTeam() {
      return this.rev?.team ?? {}
    },

    isFinancialCoach() {
      return this.account.canDoFinancialValidation
    },

    canDoFinancialValidation() {
      return Boolean(this.isFinancialCoach || access.isLeadership(this.account))
    },

    isHighlighted() {
      return Boolean(this.rev?.validation?.highlight ?? this.rev?.isHighlighted)
    },

    accountSiteId() {
      return _.get(this.account, 'site._id', '')
    },

    accountId() {
      return this.account._id
    },

    isLiked() {
      return this.rev.likedByMe
    },

    isApproved() {
      return this.rev?.isApproved
    },

    isImplementedValidated() {
      return (
        this.rev?.validations?.implementation?.isValidated ??
        this.rev?.validation?.implementation ??
        false
      )
    },

    coachIsMissing() {
      return Boolean(this.getCoachRequirement({ rev: this.rev }).isMissing)
    },

    isRevCompleted() {
      return this.rev.status === 'completed'
    },

    totalApprovalsText() {
      return this.rev?.approvals?.length > 0
        ? `(${this.rev?.approvals?.length})`
        : ''
    },

    menuOptions() {
      return _.compact([
        this.isTranslationEnabled && {
          id: 'translate',
          icon: 'translate',
          selectClass: this.isTranslated ? 'selected-item' : '',
          iconColor: this.isTranslated ? 'blue' : '',
          tid: `${this.revType}-button-translate`,
          text: this.isTranslated
            ? this.translateButtonText
            : this.$t('rev.translate_rev'),
        },
        this.permissions.approve && {
          id: this.permissions.removeApproval ? 'disapprove' : 'approve',
          icon: this.permissions.removeApproval
            ? 'mdi-check-circle'
            : 'mdi-check-circle-outline',
          selectClass: this.permissions.removeApproval ? 'selected-item' : '',
          iconColor: this.permissions.removeApproval
            ? 'blue'
            : 'blue-grey darken-2',
          tid: this.permissions.removeApproval
            ? `${this.revType}-button-disapprove`
            : `${this.revType}-button-approve`,
          text: this.permissions.removeApproval
            ? `${this.$t('common.disapproveRev')} ${this.totalApprovalsText}`
            : `${this.$t('common.approve')} ${this.totalApprovalsText}`,
          tourId: 'rev-approve-option',
        },
        {
          id: 'replicate',
          text: this.$t('rev.replication.replicate'),
          icon: 'mdi-content-duplicate',
          tid: `${this.revType}-dropdown-itemReplicate`,
        },
        {
          id: 'share',
          text: this.$t('common.button.share'),
          icon: 'mdi-share-variant-outline',
          tid: `${this.revType}-dropdown-itemShare`,
        },
        {
          id: 'history',
          text: this.$t('history.history'),
          icon: 'mdi-clock-outline',
          tid: `${this.revType}-dropdown-itemHistory`,
        },
        {
          id: 'print',
          icon: 'mdi-printer-outline',
          tid: `${this.revType}-button-print`,
          text: this.$t('rev.header.print_rev'),
        },
        this.revType === 'problemSolving' && {
          id: 'powerpoint',
          icon: 'mdi-microsoft-powerpoint',
          tid: `${this.revType}-button-powerpoint`,
          text: this.$t('rev.header.download_powerpoint'),
        },
        this.permissions.delete && {
          id: 'delete',
          text: this.$t('common.button.delete'),
          icon: 'mdi-delete-outline',
          tid: `${this.revType}-dropdown-itemDelete`,
        },
      ])
    },

    isTranslationEnabled() {
      const revLang = this.getLocaleWithoutDialect(this.rev?.lang)
      if (
        !revLang ||
        !LANGUAGES_DICTIONARY?.['primary-dialects'][revLang] ||
        revLang === this.getLocaleWithoutDialect(this.language)
      )
        return false

      return this.availableFeatures.includes('translation')
    },

    translateText() {
      if (!this.$route.query.isTranslated)
        return this.$t('rev.translate_this_rev')
      else return this.$t('rev.this_rev_has_been_translated')
    },

    translateButtonText() {
      if (!this.$route.query.isTranslated) return this.$t('common.translate')
      else
        return this.$t('common.see_original_lang', {
          lang: this.$t(`languages.${this.getRevLanguageName(this.rev)}`),
        })
    },

    hasRequestValidation() {
      return Boolean(
        this.rev?.validationRequest?.find(
          validationRequest =>
            validationRequest?.status ===
            this.$constants.validationRequestStatus.inProgress
        )
      )
    },

    changedModalStatustext() {
      return {
        title: this.$t('common.nice_try'),
        subtitle: this.showOnlyMessage
          ? this.$t('common.marked_rev_as_canceled_single_message')
          : this.$t('common.marked_rev_as_canceled'),
      }
    },

    isPlannerBadgesEnabled() {
      return this.$isFeatureEnabled(this.$featuresKeys?.plannerBadges)
    },

    showCompletedFromPlannerAlert() {
      return (
        this.isPlannerBadgesEnabled &&
        this.isRevCompleted &&
        !this.canCompleteRev
      )
    },

    linkToPlanner() {
      return `https://tasks.office.com/Home/Task/${this.plannerIntegration?.externalEntityId}`
    },
  },

  methods: {
    ...mapActions({
      setValidation: 'validations/setValidation',
      setImplementationValidation: 'validations/setImplementationValidation',
      setHighlight: 'validations/setHighlight',
      generatePowerPoint: 'problemSolving/generatePowerPoint',
    }),

    handleDownloadPowerpoint() {
      this.showPowerpointModal = true
    },

    async confirmPowerpointDownload(layout) {
      this.showPowerpointModal = false

      await this.generatePowerPoint({
        organizationId: this.organizationId,
        revId: this.revId,
        layout,
      })
    },

    handleHideChangeStatusModal() {
      this.showCancelModal = false
      this.showOnlyMessage = false
    },

    handleCancelRemoveImplementation() {
      this.trackImplementRev({
        rev: {
          type: this.revType,
          id: this.revId,
          status: this.rev.status,
        },
        action: 'Cancel',
        type: 'Remove implementation',
      })
      this.showConfirmRemoveImplementationDialog = false
    },

    handleCloseCompleteModal() {
      this.showNewCompletedModal = false
      this.trackCompleteRev({
        rev: {
          type: this.revType,
          id: this.revId,
          status: this.rev.status,
        },
        action: 'Cancel',
        type: 'Complete rev',
      })
    },

    goToStep(step, from) {
      return this.goToTab(step + 1, from)
    },

    goToReview(editMode = true, from) {
      this.showNewCompletedModal = false
      this.isEditMode = editMode
      if (this.revType === this.$constants.revTypes.quickAction)
        return this.goToStep(2, from)
      this.goToStep(4, from)
    },

    async initRevView() {
      this.setLoading()
      this.hasCompleteError = false
      this.showImplementError = false
      await this.fetchRevInExpectedLang()
      this.finishLoading()
    },

    async fetchRevInExpectedLang() {
      if (this.$route.query.isTranslated) {
        await this.translateRev()
      } else {
        await this.fetchRev()
      }
    },

    removeTeamMembers(accounts) {
      const accountsIds = accounts.map(account => account?._id ?? account?.id)
      const revTeamIds = _.chain(this.rev.team)
        .values()
        .flatten()
        .map(member => member?.id ?? null)
        .value()
      return _.filter(
        accountsIds,
        accountId => !revTeamIds.includes(accountId)
      )
    },

    hasAvailableTeamMembers(accounts) {
      const availableMembers = this.removeTeamMembers(accounts)
      return Boolean(availableMembers.length)
    },

    async fetchRev() {
      try {
        return await this.get({
          revId: this.revId,
          organizationId: this.organizationId,
        })
      } catch (err) {
        this.showNotFound = true
      }
    },

    finishRev(data) {
      this.finishedRev = true
      this.saveRev(data)
    },

    trackStatusChanged(status, reason) {
      this.trackRevStatusChanged(
        this.rev,
        this.isEditMode,
        'selector',
        status,
        reason
      )
    },

    async updateStatus(payload) {
      try {
        await this.update({
          organizationId: this.organizationId,
          revId: this.rev.id,
          data: { ...this.revToUpdate, ...payload },
        })
        this.showSuccess(this.$t('rev.save_success'))
        this.trackStatusChanged(payload.status, {
          canceledReason: Boolean(payload.reason),
        })
        this.setViewMode()
        this.resetRevChanged()
        await this.get({
          organizationId: this.organizationId,
          revId: this.rev.id,
        })
      } catch (err) {
        this.handleRequestError(err)
      } finally {
        this.revToUpdate = {}
        this.isSaving = false
        this.showCancelModal = false
      }
    },

    handleModalStatusChanged(payload) {
      this.updateStatus({
        canceledReason: payload.reason,
        status: payload.status,
      })
    },

    async saveRev(data) {
      try {
        this.isSaving = true
        const revId = this.rev.id
        const hasSite = !!this.rev.location.site

        if (
          !this.canCompleteRev &&
          this.isRevCompleted &&
          this.isPlannerBadgesEnabled
        ) {
          this.hasCompleteError = true
          return this.showError(this.$t('common.complete_error'))
        }

        if (!hasSite) {
          this.errors.site = true
          return
        }
        this.errors.site = false

        if (
          this.revType === 'improvementIdea' &&
          !data?.verify?.didExperimentWork &&
          data?.status === this.$constants.revStatus.completed
        ) {
          this.revToUpdate = data
          this.showCancelModal = true
          this.showOnlyMessage = true
          return
        }

        await this.update({
          organizationId: this.organizationId,
          revId,
          data: data || this.rev,
        })

        this.showSuccess(this.$t('rev.save_success'))
        this.setViewMode()
        this.resetRevChanged()

        await this.get({
          organizationId: this.organizationId,
          revId,
        })
      } catch (err) {
        this.handleRequestError(err)
      } finally {
        this.isSaving = false
      }
    },

    handleCancelClick() {
      if (this.revChanged) {
        const leavePage = window.confirm(this.$t('rev.cancel'))

        if (leavePage) {
          this.cancelRevSave()
          this.resetRev(this.initialRev)
        }
      } else {
        this.resetRev(this.initialRev)
        this.setViewMode()
      }
    },

    cancelRevSave() {
      this.resetRevChanged()
      this.setViewMode()
      this.errors.site = false
    },

    resetRevChanged() {
      this.revChanged = false
    },

    setViewMode() {
      this.isEditMode = false
    },

    async setEditMode() {
      this.initialRev = _.cloneDeep(this.rev)
      this.isEditMode = true
    },

    setRevChanged() {
      this.revChanged = true
    },

    setLoading() {
      this.isLoading = true
    },

    finishLoading() {
      this.isLoading = false
    },

    toggleTranslations() {
      if (this.$route.query.isTranslated) {
        this.getRevInOriginalLang()
      } else {
        this.translateRev()
      }
    },

    async translateRev() {
      try {
        this.isTranslating = true
        await this.translate()
        this.$router
          .replace({
            path: this.$route.path,
            query: { isTranslated: true },
          })
          .catch(() => {})
        this.trackTranslationAlert()
      } catch (err) {
        this.handleRequestError(err)
      } finally {
        this.isTranslating = false
      }
    },

    async getRevInOriginalLang() {
      try {
        this.isTranslating = true
        await this.get({
          organizationId: this.organizationId,
          revId: this.revId,
        })
        this.$router
          .replace({
            path: this.$route.path,
            query: {
              ..._.omit(this.$route.query, 'isTranslated'),
            },
          })
          .catch(() => {})
      } catch (err) {
        this.handleRequestError(err)
      } finally {
        this.isTranslating = false
      }
    },

    likeRev() {
      this.isSaving = true
      this.like({
        organizationId: this.organizationId,
        revId: this.rev.id,
      })
        .then(() => {
          this.trackLike(this.rev, this.revType, true, 'success')
          this.isSaving = false
        })
        .catch(err => {
          this.trackLike(this.rev, this.revType, true, 'fail', err)
          this.isSaving = false
        })
    },

    unlikeRev() {
      this.isSaving = true
      this.unlike({
        organizationId: this.organizationId,
        revId: this.rev.id,
      })
        .then(() => {
          this.trackLike(this.rev, this.revType, false, 'success')
          this.isSaving = false
        })
        .catch(err => {
          this.trackLike(this.rev, this.revType, false, 'fail', err)
          this.isSaving = false
        })
    },

    async handleEditMode(rev) {
      if (this.isEditMode) {
        await this.saveRev(rev)
      } else
        await this.get({
          organizationId: this.organizationId,
          revId: this.revId,
        })
    },

    async handleImplementRev(rev) {
      this.trackImplementRev({
        rev: {
          type: this.revType,
          id: this.revId,
          status: this.rev.status,
        },
        action: 'button click',
        type: this.isImplementedValidated
          ? 'remove implementation'
          : 'implement',
        errors: {
          revIsNotCompleted: !this.isRevCompleted,
          userCannotImplement: !this.permissions.implement,
        },
      })

      if (!this.isRevCompleted) {
        if (!this.canCompleteRev) this.hasCompleteError = true
        this.showImplementError = true
        return this.showError(this.$t('rev.implement_error'))
      }

      if (!this.permissions.implement) return

      if (this.isImplementedValidated) {
        return (this.showConfirmRemoveImplementationDialog = true)
      }

      await this.implementRev(rev)

      this.goToReview(false, 'implementModal')
    },

    launchRocket() {
      this.$refs.rocket.launchRocket()
    },

    async implementRev(rev, from) {
      try {
        this.isSaving = true
        const value = !this.isImplementedValidated

        const revType =
          this.revType === 'improvementIdea'
            ? 'rev'
            : this.revType.replace('-', '').toLowerCase()

        const data = {
          isValidated: this.isImplementedValidated,
          revType,
          validationId:
            this.rev?.validations?.implementation?.id ??
            this.rev?.validation?.implementationId,
        }

        await this.setImplementationValidation({
          revId: this.revId,
          organizationId: this.organizationId,
          ...data,
        })

        await this.handleEditMode({ ...this.rev, ...rev, ...data })

        this.trackUpdate(
          { ...this.rev, type: this.revType },
          'implementation',
          value,
          from
        )
        this.isSaving = false
        if (value) this.launchRocket()
        return value
          ? this.showSuccess(this.$t('rev.rev_implemented'))
          : this.showSuccess(this.$t('rev.rev_implementation_removed'))
      } catch (err) {
        this.handleRequestError(err)
      } finally {
        this.isSaving = false
        this.showConfirmRemoveImplementationDialog = false
      }
    },

    async highlightRev(rev) {
      try {
        this.isSaving = true
        const value = !this.isHighlighted
        const revType =
          this.revType === 'improvementIdea'
            ? 'rev'
            : this.revType.replace('-', '').toLowerCase()
        await this.setHighlight({
          revId: this.revId,
          organizationId: this.organizationId,
          isHighlighted: value,
          revType,
        })
        this.handleEditMode(rev)

        this.trackUpdate(
          { ...this.rev, type: this.revType },
          'highlight',
          value
        )
        this.isSaving = false
        return value
          ? this.showSuccess(this.$t('rev.rev_highlighted'))
          : this.showSuccess(this.$t('rev.rev_highlight_removed'))
      } catch (err) {
        this.isSaving = false
        this.handleRequestError(err)
      }
    },

    async approveRev() {
      try {
        this.isSaving = true
        const response = await this.approve({
          organizationId: this.organizationId,
          revId: this.rev.id,
        })
        this.trackApprovals(this.rev, this.revType, 'done', 'added')
        this.isSaving = false

        this.showSuccess(this.$t('rev.approved_successfully'))
        return response.data
      } catch (err) {
        this.isSaving = false
        this.handleRequestError(err)
      }
    },

    async disapproveRev() {
      try {
        this.isSaving = true
        const response = await this.disapprove({
          organizationId: this.organizationId,
          revId: this.rev.id,
          approvalId: this.rev.approvedByMe,
        })
        this.trackApprovals(this.rev, this.revType, 'done', 'removed')
        this.isSaving = false
        this.showSuccess(this.$t('rev.removed_successfully'))
        return response.data
      } catch (err) {
        this.isSaving = false
        this.handleRequestError(err)
      }
    },

    handleCompleteRev(canCompleteRev) {
      this.trackCompleteRev({
        rev: {
          type: this.revType,
          id: this.revId,
          status: this.rev.status,
        },
        action: 'Button click',
        type: 'Complete rev',
        errors: {
          hasMissingFields: !canCompleteRev,
          coachIsMissingAndRequired:
            this.coachIsMissing && this.coachIsRequired,
        },
      })

      if (this.isRevCompleted) return

      if (!canCompleteRev) {
        this.hasCompleteError = true
        const didExperimentWork = this.rev.verify?.didExperimentWork
        if (
          !didExperimentWork &&
          this.revType === this.$constants.revTypes.improvementIdea
        )
          return this.showError(this.$t('rev.errorDidNotWork'))

        return this.showError(this.$t('common.complete_error'))
      }

      if (this.coachIsMissing && this.coachIsRequired)
        return (this.showMissingCoachDialog = true)
      else {
        this.completeRev()
      }

      this.trackRevStatusChanged(
        this.rev,
        this.isEditMode,
        'button',
        'completed',
        null
      )
    },

    async completeRev() {
      try {
        await this.complete()
        this.showMissingCoachDialog = false
        this.revChanged = false
        this.showImplementError = false
        this.setCompleteAnimation()
        this.showNewCompletedModal = true
      } catch (err) {
        this.handleRequestError(err)
      } finally {
        this.setViewMode()
      }
    },

    setCompleteAnimation() {
      confetti({
        particleCount: 200,
        spread: 90,
        origin: { y: 0.55 },
        zIndex: 201,
      })
    },

    checkChangesBeforeLeaving({ next }) {
      if (this.isEditMode && this.revChanged) {
        if (window.confirm(this.$t('common.sure_to_leave_page'))) {
          this.cancelRevSave()
          return next()
        } else {
          return next(false)
        }
      }

      next()
    },

    async setValidationRequest(assignee, rev) {
      try {
        const payload = {
          organizationId: this.organizationId,
          revId: this.rev.id,
          assignees: _.compact([assignee]),
        }
        await this.createValidationRequest(payload)
        this.handleEditMode(rev)

        this.showSuccess(this.$t('rev.validation_request_successfully'))
      } catch (err) {
        this.handleRequestError(err)
      }
    },

    async setCancelRequest(payload, rev) {
      try {
        await this.cancelValidationRequest({
          organizationId: this.organizationId,
          revId: this.rev.id,
          requestId: payload.requestId,
          status: payload.status,
        })

        if (this.isEditMode) {
          this.handleEditMode(rev)
        } else {
          await this.get({
            organizationId: this.organizationId,
            revId: this.rev.id,
          })
        }

        this.showSuccess(this.$t('rev.validation_request_canceled'))
      } catch (err) {
        this.handleRequestError(err)
      }
    },

    async handleSetCoach(coach) {
      if (coach) coach.coaching = coach.coaching + 1
      await this.setCoach(coach)
      await this.saveRev()
      this.trackUpdate(
        { ...this.rev, type: this.revType },
        'coach',
        coach,
        'completeDialog'
      )
    },

    async getRevIntegrations() {
      const { data } = await api.integrations.getIntegrationsRelations({
        organizationId: this.organizationId,
        filters: { entityId: this.revId, externalPlatformName: 'planner' },
      })

      this.plannerIntegration = data?.results?.[0] ?? null
    },

    handleSummaryActionable() {
      eventHub.$emit(HUB_EVENTS.AI.HUB_DIALOG_OPEN, {
        rev: this.rev,
        hasFloatingTrigger: true,
        showMultipleActions: true,
        createSummary: this.isRevCompleted,
        suggestTasks: this.rev.status !== this.$constants.revStatus.canceled,
      })
    },

    replicateRev(revData) {
      eventHub.$emit(HUB_EVENTS.REV_VIEW.REPLICATE_REV, _.cloneDeep(revData))
    },
  },

  beforeDestroy() {
    this.resetRev()
    eventHub.$off(
      HUB_EVENTS.REV_VIEW.BEFORE_ROUTE_LEAVE,
      this.checkChangesBeforeLeaving
    )
  },
}
</script>

<style lang="scss" scoped>
.planner-alert {
  background-color: #216733;
  height: 34px;
  border-radius: 4px;
  box-shadow:
    0px 3px 1px -2px rgba(45, 55, 72, 0.2),
    0px 2px 2px 0px rgba(45, 55, 72, 0.14),
    0px 1px 5px 0px rgba(45, 55, 72, 0.12) !important;

  .planner-icon {
    width: 17px;
  }

  .link-to-planner {
    text-decoration: none;
  }
}
</style>
